import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { shopApi, type ShopOrderState } from '@/services/shop.api';

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 5 * 60 * 1000;

const isSettled = (state: ShopOrderState | undefined): boolean => state === 'activated' || state === 'completed_unlinked' || state === 'needs_review';

/**
 * Add-on-only purchases (no Early Supporter checkout id): polls the provisioning state of the WC
 * order every 3 s until activated / needs_review or 5 minutes pass (`timedOut`).
 */
export const useShopOrderStatus = (orderId: number | null) => {
  const startedAt = useRef<number>(Date.now());
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    startedAt.current = Date.now();
    setTimedOut(false);
  }, [orderId]);

  const query = useQuery({
    queryKey: ['shop-order-status', orderId],
    queryFn: () => shopApi.getOrderStatus(orderId as number),
    enabled: orderId !== null && !timedOut,
    retry: 1,
    gcTime: 0,
    refetchIntervalInBackground: true,
    refetchInterval: (q) => {
      if (isSettled(q.state.data?.state)) return false;
      if (Date.now() - startedAt.current > POLL_MAX_MS) return false;
      return POLL_INTERVAL_MS;
    },
  });

  const state = query.data?.state;
  const settled = isSettled(state);

  useEffect(() => {
    if (orderId === null || settled) return;
    const remaining = POLL_MAX_MS - (Date.now() - startedAt.current);
    const timer = window.setTimeout(() => setTimedOut(true), Math.max(remaining, 0));
    return () => window.clearTimeout(timer);
  }, [orderId, settled, timedOut]);

  /** "Check again" after a timeout: restart the 5 minute polling window. */
  const restart = () => {
    startedAt.current = Date.now();
    setTimedOut(false);
  };

  return { state, timedOut: timedOut && !settled, restart };
};
